/**
 * Composable per Activities autocomplete con loading e error handling centralizzati
 *
 * Fornisce funzionalità di ricerca asincrona di attività (Activity) con OData query building
 * e loading state automatico. Pronto per integrazione in autocomplete input field.
 *
 * @param options - Opzioni configurazione (withLoading per loading state condiviso)
 * @returns Oggetto con activities reactive array e metodi di ricerca/pulizia
 */

import { ref, readonly } from 'vue';
import { useODataBuilder } from '../useODataBuilder';
import { useLoadingState } from '../useLoadingState';
import { useNotification } from '../useNotification';
import { useStaleSafeRequest } from '../useStaleSafeRequest';
import type { BaseAutocompleteComposableOptions } from './types';
import type { ActivitiesAutocompleteOptions } from '@/lib/autocompletes/activities';
import type { ActivityAutocompleteResult } from '@/lib/models/activity';
import { getActivitiesAutocomplete } from '@/lib/autocompletes/activities';

export function useActivitiesAutocomplete(options: BaseAutocompleteComposableOptions = {}) {
	const activities = ref<ActivityAutocompleteResult[]>([]);

	const { buildOdataQuery } = useODataBuilder();
	// Usa withLoading iniettato dal parent, fallback su istanza locale se non fornito
	const withLoadingFn = options.withLoading || useLoadingState().withLoading;
	const { error } = useNotification();
	const { requestToken } = useStaleSafeRequest();

	async function searchActivities(options: ActivitiesAutocompleteOptions = {}): Promise<void> {
		const token = requestToken();
		await withLoadingFn(async () => {
			try {
				const result = await getActivitiesAutocomplete(buildOdataQuery, options);
				if (token.isLatest()) {
					activities.value = result;
				}
			} catch (err) {
				console.error('❌ ERROR useActivitiesAutocomplete.searchActivities:', err);
				error('Errore durante il caricamento delle attività! Ricaricare la pagina e riprovare.');
				if (token.isLatest()) {
					activities.value = [];
				}
			}
		}, 'searchActivities');
	}

	function clearActivities(): void {
		activities.value = [];
	}

	return {
		activities: readonly(activities),
		searchActivities,
		clearActivities,
	};
}
